import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import {
  QUERY_GET_MANAGEMENT_NOTES,
  useManagementCreateNote,
  useManagementDeleteNote,
  useManagementGetNote,
  useManagementUpdateNote
} from '@apps-common/apis/management/note'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { TextAreaSM } from '@apps-common/components/elements/form'
import { VertSpacerMd } from '@apps-common/components/layout'
import { ConfirmModalAction } from '@apps-common/components/modals'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer,
  ModalTopConfirmActionButton
} from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { queryClient } from '@apps-common/lib'
import { INoteModel } from '@apps-common/types/management/note'

export interface IManagementNoteModalProps {
  entityId: string
  noteId?: string
}

export const ManagementNoteModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IManagementNoteModalProps
}) => {
  const { entityId, noteId } = componentProps
  const isEditing = noteId !== undefined

  const {
    data: note,
    isLoading: noteIsLoading,
    isError: noteIsError
  } = useManagementGetNote({
    config: {
      enabled: noteId !== undefined
    },
    noteId: noteId || ''
  })

  const refetchQueries = (role: string) => {
    queryClient.refetchQueries({ queryKey: QUERY_GET_MANAGEMENT_NOTES(entityId) })
    onDismiss(null, role)
  }

  const { mutate: create, isPending: createIsPending } = useManagementCreateNote(true, () => refetchQueries(ModalDismiss.Confirm))
  const { mutate: update, isPending: updateIsPending } = useManagementUpdateNote(true, () => refetchQueries(ModalDismiss.Confirm))
  const { mutate: deleteNote, isPending: deleteIsPending } = useManagementDeleteNote(true, () => refetchQueries(ModalDismiss.Confirm))

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<INoteModel>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      id: 0,
      entityId,
      noteContent: '',
      dateEdited: new Date().toISOString(),
      userId: 0
    }
  })

  useEffect(() => {
    if (note) {
      reset(note)
    }
  }, [note, reset])

  const submit = (values: INoteModel) => {
    if (noteId) {
      update({ model: values })
    } else {
      create({ model: values })
    }
  }
  const DeleteIcon = commonIcons.delete

  const isLoading = noteIsLoading || noteIsError
  const isUpdating = createIsPending || updateIsPending

  return (
    <ModalContainer innerClasses='!min-h-0 !h-auto sm:max-w-[400px]'>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle={isEditing ? 'Update' : 'Create'} title='Management note' />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
      </ModalHeaderContainer>

      <ModalBody classes='!pt-[95px]'>
        {isEditing && (
          <div className='flex items-center justify-end mb-[20px]'>
            <ModalTopConfirmActionButton
              loading={isLoading || isUpdating}
              confirmProps={{
                text: 'Delete note?',
                description: 'Are you sure you would like to delete this note?',
                action: ConfirmModalAction.Error
              }}
              confirmCallback={() => deleteNote({ noteId: note?.noteId || '' })}
              text='Delete'
              icon='delete'
            />
          </div>
        )}
        <form noValidate id='unit-management-note-modify-form' onSubmit={handleSubmit(submit)}>
          <TextAreaSM
            key='portal-note-content'
            type='text'
            placeholder='Enter note contents'
            label='Note contents'
            id='portal-note-content'
            loading={isLoading}
            disabled={isUpdating}
            className='min-h-[180px]'
            registration={register('noteContent', {
              required: true,
              maxLength: {
                value: 300,
                message: 'No longer than 300 characters '
              }
            })}
            errors={errors.noteContent}
          />
          <VertSpacerMd />
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-[10px]'>
          <ButtonRoundedSm
            type='submit'
            form='unit-management-note-modify-form'
            loading={isLoading || isUpdating}
            className='action-success'
            text={isEditing ? 'Update' : 'Create'}
          />
          <ButtonRoundedSm
            type='button'
            loading={isLoading || isUpdating}
            className='action-neutral'
            text='Cancel'
            callback={() => onDismiss(null, ModalDismiss.Cancel)}
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
